/**
 * Generates Mermaid TD (top-down) flowchart from C2PA ManifestStore data.
 *
 * Adapted for Phenom Drop UI: top-down layout with Phenom color palette.
 *
 * Node classes:
 *   current  - cyan    - the active (current) manifest being analysed
 *   origin   - red     - the originating (parent) ingredient
 *   valid    - green   - ingredient added later with valid C2PA
 *   invalid  - red     - ingredient with validation failures
 *   unsigned - grey    - ingredient with no C2PA data
 */

interface ManifestShape {
  title?: string | null
  format?: string | null
  claim_generator?: string | null
  claim_generator_info?: Array<{ name?: string; version?: string | null }> | null
  ingredients?: IngredientShape[]
  label?: string | null
  [k: string]: unknown
}

interface IngredientShape {
  title?: string | null
  format?: string | null
  relationship?: string | null
  active_manifest?: string | null
  validation_status?: Array<{ code: string; success?: boolean | null }> | null
  validation_results?: {
    activeManifest?: {
      failure?: Array<{ code: string }> | null
    } | null
  } | null
}

interface ManifestStoreShape {
  active_manifest?: string | null
  manifests?: { [k: string]: ManifestShape }
}

function sanitize(text: string): string {
  return text
    .replace(/\\/g, '')
    .replace(/"/g, "'")
    .replace(/[<>{}[\]|#;`]/g, '')
    .replace(/[\n\r\t]/g, ' ')
    .trim()
}

function truncate(text: string, max = 30): string {
  return text.length <= max ? text : text.substring(0, max - 1) + '\u2026'
}

function formatMime(mime: string | null | undefined): string {
  if (!mime) return ''
  return (mime.split('/')[1] || mime).toUpperCase().replace('+', ' ')
}

function extractToolName(manifest: ManifestShape): string {
  if (manifest.claim_generator_info && manifest.claim_generator_info.length > 0) {
    const info = manifest.claim_generator_info[0]
    if (info.name) {
      const name = info.name
      const short = name.replace(/^(Adobe|Google|Microsoft|Apple|Samsung)\s+/i, '')
      return sanitize(truncate(short, 18))
    }
  }
  if (manifest.claim_generator) {
    const cg = manifest.claim_generator
    const parts = cg.split(/\s+/)
    for (let i = parts.length - 1; i >= 0; i--) {
      const name = parts[i].split('/')[0]
      if (name && !name.match(/^c2pa[-_]?(rs|js)?$/i)) {
        return sanitize(truncate(name, 18))
      }
    }
    const first = parts[0]?.split('/')[0]
    if (first) return sanitize(truncate(first, 18))
  }
  return ''
}

function buildManifestLabel(
  manifest: ManifestShape,
  titleCounts: Map<string, number>,
  addedCount?: number,
  stepNum?: number
): string {
  const rawTitle = sanitize(truncate(manifest.title || '', 24))
  const baseTitle = rawTitle || (stepNum != null ? `Step ${stepNum}` : 'Step')
  const mime = formatMime(manifest.format)
  const titleKey = `${rawTitle || ''}|${mime}`

  let label: string

  if ((titleCounts.get(titleKey) || 0) > 1) {
    const tool = extractToolName(manifest)
    if (tool) {
      label = mime ? `${baseTitle} - ${tool} (${mime})` : `${baseTitle} - ${tool}`
    } else {
      const displayTitle = rawTitle ? baseTitle : (stepNum != null ? `Step ${stepNum}` : baseTitle)
      label = mime ? `${displayTitle} (${mime})` : displayTitle
    }
  } else {
    label = mime ? `${baseTitle} (${mime})` : baseTitle
  }

  if (addedCount && addedCount > 0) {
    label += ` [+${addedCount} added]`
  }

  return label
}

function ingredientClass(ing: IngredientShape): string {
  const failures = ing.validation_results?.activeManifest?.failure
  if (failures && failures.length > 0) return 'invalid'

  if (ing.validation_status && ing.validation_status.length > 0) {
    const hasError = ing.validation_status.some(
      s => s.success === false || s.code?.includes('hash.mismatch')
    )
    if (hasError) return 'invalid'
  }

  if (ing.active_manifest) return 'valid'
  return 'unsigned'
}

function relationshipLabel(rel: string | null | undefined, isLeaf: boolean): string {
  if (rel === 'parentOf') return isLeaf ? 'original' : 'edited'
  if (rel === 'componentOf') return 'added'
  if (rel === 'inputTo') return 'added'
  return 'added'
}

function nodeLabel(title: string | null | undefined, format: string | null | undefined, ingredientNum?: number): string {
  const raw = sanitize(truncate(title || ''))
  const t = raw || (ingredientNum != null ? `Ingredient ${ingredientNum}` : 'Ingredient')
  const f = formatMime(format)
  return f ? `${t} (${f})` : t
}

function countTitles(manifests: { [k: string]: ManifestShape }): Map<string, number> {
  const counts = new Map<string, number>()
  for (const key of Object.keys(manifests)) {
    const m = manifests[key]
    const rawTitle = sanitize(truncate(m.title || '', 24))
    const mime = formatMime(m.format)
    const titleKey = `${rawTitle || ''}|${mime}`
    counts.set(titleKey, (counts.get(titleKey) || 0) + 1)
  }
  return counts
}

/**
 * Generate Mermaid TD (top-down) flowchart from a ManifestStore.
 * Returns an empty string if there is nothing to diagram.
 *
 * Uses Phenom color palette: cyan for current, red for origin,
 * green for valid, red for invalid, gray for unsigned.
 */
export function generateMermaidDiagram(store: ManifestStoreShape): string {
  const manifests = store.manifests
  const activeKey = store.active_manifest

  if (!manifests || !activeKey || !manifests[activeKey]) return ''

  const activeManifest = manifests[activeKey]
  if (!activeManifest.ingredients || activeManifest.ingredients.length === 0) return ''

  const titleCounts = countTitles(manifests)
  const lines: string[] = ['graph TD']
  const visited = new Set<string>()
  let counter = 0
  let ingredientCounter = 0
  const nodeMap = new Map<string, string>()
  const classOverrides = new Map<string, string>()

  function nextId(): string {
    return `n${counter++}`
  }

  function manifestNodeId(key: string): string {
    if (!nodeMap.has(key)) nodeMap.set(key, nextId())
    return nodeMap.get(key)!
  }

  let stepCounter = 0

  function processManifest(key: string, isRoot: boolean): void {
    if (visited.has(key)) return
    visited.add(key)

    const manifest = manifests![key]
    if (!manifest) return

    stepCounter++
    const stepNum = stepCounter

    if (!manifest.ingredients || manifest.ingredients.length === 0) {
      const nodeId = manifestNodeId(key)
      const label = buildManifestLabel(manifest, titleCounts, undefined, stepNum)
      const cls = isRoot ? 'current' : (classOverrides.get(key) || 'valid')
      lines.push(`  ${nodeId}("${label}"):::${cls}`)
      return
    }

    const parentIngredient = manifest.ingredients.find(ing => ing.relationship === 'parentOf')
    const addedIngredients = manifest.ingredients.filter(ing => ing.relationship !== 'parentOf')
    const addedCount = addedIngredients.length

    const nodeId = manifestNodeId(key)
    const label = buildManifestLabel(manifest, titleCounts, addedCount, stepNum)
    const cls = isRoot ? 'current' : (classOverrides.get(key) || 'valid')
    lines.push(`  ${nodeId}("${label}"):::${cls}`)

    if (parentIngredient) {
      const ingCls = 'origin'
      let ingNodeId: string
      let isLeaf = true

      if (parentIngredient.active_manifest && manifests![parentIngredient.active_manifest]) {
        const linkedManifest = manifests![parentIngredient.active_manifest]
        isLeaf = !linkedManifest.ingredients || linkedManifest.ingredients.length === 0
        ingNodeId = manifestNodeId(parentIngredient.active_manifest)
        if (!classOverrides.has(parentIngredient.active_manifest)) {
          classOverrides.set(parentIngredient.active_manifest, ingCls)
        }
        if (!visited.has(parentIngredient.active_manifest)) {
          processManifest(parentIngredient.active_manifest, false)
        }
      } else {
        ingredientCounter++
        ingNodeId = nextId()
        const ingLabel = nodeLabel(parentIngredient.title, parentIngredient.format, ingredientCounter)
        lines.push(`  ${ingNodeId}("${ingLabel}"):::${ingCls}`)
      }

      const edgeLabel = relationshipLabel(parentIngredient.relationship, isLeaf)
      lines.push(`  ${ingNodeId} -->|"${edgeLabel}"| ${nodeId}`)
    }

    if (!parentIngredient && addedIngredients.length > 0) {
      const firstAdded = addedIngredients[0]
      const ingCls = ingredientClass(firstAdded)
      let ingNodeId: string

      if (firstAdded.active_manifest && manifests![firstAdded.active_manifest]) {
        ingNodeId = manifestNodeId(firstAdded.active_manifest)
        if (!classOverrides.has(firstAdded.active_manifest)) {
          classOverrides.set(firstAdded.active_manifest, ingCls)
        }
        if (!visited.has(firstAdded.active_manifest)) {
          processManifest(firstAdded.active_manifest, false)
        }
      } else {
        ingredientCounter++
        ingNodeId = nextId()
        const summaryLabel = addedIngredients.length === 1
          ? nodeLabel(firstAdded.title, firstAdded.format, ingredientCounter)
          : `${addedIngredients.length} ingredients`
        lines.push(`  ${ingNodeId}("${summaryLabel}"):::${ingCls}`)
      }

      lines.push(`  ${ingNodeId} -->|"added"| ${nodeId}`)
    }
  }

  processManifest(activeKey, true)

  // Phenom color palette
  lines.push('  classDef current fill:#a5e3e8,stroke:#7ec8cf,color:#121010')
  lines.push('  classDef origin fill:#d73429,stroke:#b82a21,color:#fff')
  lines.push('  classDef valid fill:#10b981,stroke:#047857,color:#fff')
  lines.push('  classDef invalid fill:#ef4444,stroke:#b91c1c,color:#fff')
  lines.push('  classDef unsigned fill:#6b7280,stroke:#4b5563,color:#fff')

  return lines.join('\n')
}
